import type { Locale } from './locale.ts';

/**
 * MVP-291. Every date, time, number and language name the app renders is built here, from the
 * app's OWN locale — the one the KJ picked in Settings → ภาษา — rather than from the browser's.
 *
 * Before this, every formatter in the app was constructed with `undefined` as its locale, which
 * means "whatever the browser is set to". The result was a Thai UI printing US English dates: the
 * Activity log read "Sep 6, 10:09 PM" and an account row read "ใช้งานถึง 12/31/2026". The sentence
 * was translated; the date inside it was not.
 *
 * This module is deliberately pure `.ts` with no React and no DOM, so `formats.test.ts` can pin it
 * under vitest's node environment — and so `api/catalog.ts` can stay free of any i18n import by
 * taking a resolved tag string instead (see `languageDisplayName` there).
 */

/**
 * WHICH CALENDAR, decided by the owner on 2026-09-08.
 *
 * `Intl` with a bare `th-TH` renders the Buddhist era — พ.ศ. 2569, not 2026. The owner's call was
 * to split it by who is reading:
 *
 *  - `audience`: the guest request page and the Stage. Thai customers read พ.ศ. on their own
 *    paperwork, and nothing on those screens is ever checked against a server log.
 *  - `admin`: the Activity log, Accounts, Invites. These are read side by side with things that
 *    only speak Gregorian — the audit rows the server wrote, an ISO timestamp in a bug report, a
 *    bank statement — and an operator reconciling 2569 against 2026 is a mistake waiting to happen.
 *
 * English is unaffected: there is no second calendar to choose between.
 *
 * No audience surface formats a date TODAY (`features/guest` and `features/ops` render positions,
 * ETAs and countdowns, never an absolute date). The branch exists so the first one that does lands
 * in the right calendar by construction rather than by whoever writes it remembering this decision.
 */
export type Surface = 'admin' | 'audience';

/** The tag for anything where the calendar is irrelevant — language names, number grouping. */
const BASE_TAG: Record<Locale, string> = { th: 'th-TH', en: 'en' };

export function displayLocale(locale: Locale): string {
  return BASE_TAG[locale];
}

/** The tag for anything that renders a YEAR. `-u-ca-gregory` is the Unicode extension that keeps
 * Thai month names and Thai digitless numerals while pinning the era to Gregorian. */
export function calendarLocale(locale: Locale, surface: Surface): string {
  return locale === 'th' && surface === 'admin' ? 'th-TH-u-ca-gregory' : BASE_TAG[locale];
}

/**
 * Option bags rather than a locale's own default style. `toLocaleDateString()` with no options is
 * where "12/31/2026" came from: in `en` the default short style is month-first, which contradicts
 * every other date this app prints ("6 Sep") and is ambiguous to anyone who reads 1/12 as December.
 * Naming the fields explicitly means the SHAPE of a date never varies by locale, only its words.
 */
const DATE_TIME_OPTIONS: Intl.DateTimeFormatOptions = {
  day: 'numeric',
  month: 'short',
  hour: '2-digit',
  minute: '2-digit',
};

const DATE_OPTIONS: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'short', year: 'numeric' };

/**
 * These used to be module-level constants built once at import — which is exactly why they could
 * not read a locale that changes at runtime. Caching by tag keeps that cost (an `Intl` constructor
 * is not cheap, and the Activity log builds one per row) without freezing the answer: a language
 * switch asks for a different tag and gets a different formatter, with no reload.
 */
const dateTimeFormats = new Map<string, Intl.DateTimeFormat>();
const dateFormats = new Map<string, Intl.DateTimeFormat>();
const numberFormats = new Map<string, Intl.NumberFormat>();

function cached<T>(store: Map<string, T>, tag: string, make: () => T): T {
  const hit = store.get(tag);
  if (hit) return hit;
  const made = make();
  store.set(tag, made);
  return made;
}

/** "6 ก.ย. 10:04". No year — a log read top-down is about recency. */
export function formatDateTime(date: Date, locale: Locale, surface: Surface): string {
  const tag = calendarLocale(locale, surface);
  return cached(dateTimeFormats, tag, () => new Intl.DateTimeFormat(tag, DATE_TIME_OPTIONS)).format(date);
}

/** "6 ก.ย. 2026" (admin) / "6 ก.ย. 2569" (audience). */
export function formatDate(date: Date, locale: Locale, surface: Surface): string {
  const tag = calendarLocale(locale, surface);
  return cached(dateFormats, tag, () => new Intl.DateTimeFormat(tag, DATE_OPTIONS)).format(date);
}

/** DESIGN.md §7's "counts with locale grouping (104,912 songs)", from the app's locale rather than
 * the browser's — a browser set to German would otherwise print 104.912 inside a Thai sentence. */
export function formatCount(value: number, locale: Locale): string {
  const tag = displayLocale(locale);
  return cached(numberFormats, tag, () => new Intl.NumberFormat(tag)).format(value);
}
